/* eslint-disable jsx-a11y/anchor-is-valid */
import styles from "./SectionCardListItem.module.scss";
import primaryComponents from "../../../components/primaryComponents";
import secondaryComponents from "..";
import { SectionCardLogic } from "../SectionCard/SectionCardLogic";
import { truncateString, defaultDateDisplay } from "../../../helpers";
import { parseDurationToSeconds } from "../SectionDurationBadge/utils";
import React, { useMemo } from "react";
import { SectionView } from "../../printViews";
import TransactionDetailsV2 from "../StoreCheckout/TransactionDetailsV2";
import PrintTypeOption from "../StoreCheckout/PrintTypeOption";
import ReceiptTerminalsV2 from "../StoreCheckout/ReceiptTerminalsV2";
import { useQueryClient } from "react-query";

const SectionCardListItem = ({ sectionOrder, autoScrollRef }) => {
  const {
    sectionDetails,
    orderId,
    isEnabledPrint,
    isPrintModal,
    isTerminal,
    setIsTerminal,
    setIsPrintModal,
    setIsEnabledPrint,
    setOrderId,
    setSectionDetails,
  } = SectionCardLogic();
  const queryClient = useQueryClient();
  const sectionStatus = sectionOrder?.status;
  const durMins = useMemo(
    () =>
      Math.round(parseDurationToSeconds(sectionOrder?.expected_duration) / 60),
    [sectionOrder?.expected_duration],
  );

  const invalidateQueries = () => {
    queryClient.invalidateQueries(["sectionOrders"]);
  };

  const statusClass =
    sectionStatus === "RECEIVED"
      ? styles.received
      : sectionStatus === "IN_PROGRESS"
        ? styles.progress
        : sectionStatus === "COMPLETED"
          ? styles.completed
          : "";

  return (
    <>
      <div className={`${styles.listItem} ${statusClass}`} ref={autoScrollRef}>
        {/* Top row: queue + badge + table — transparent on desktop, flex row on mobile */}
        <div className={styles.listItem__topRow}>
          <div className={styles.listItem__queue}>
            <h1>{sectionOrder?.order?.queue_number}</h1>
          </div>

          <div className={styles.listItem__divider} />

          {/* Type badge */}
          <div className={styles.listItem__badge}>
            <p className="smallest-text-size pending--rounded mb-0">
              {(sectionOrder?.allow_order_type ||
                sectionOrder?.order?.order_source === "INTERNAL") &&
                sectionOrder?.order?.order_type?.replace("_", " ")}{" "}
              (
              {sectionOrder?.order?.order_source === "INTERNAL"
                ? "SELL"
                : "ONLINE"}
              )
            </p>
          </div>

          <div className={styles.listItem__divider} />

          {/* Table */}
          <div className={styles.listItem__table}>
            <p className="small-text-size mb-0">Table:</p>
            <p className="dark--text mb-0">
              {sectionOrder?.order?.table?.name || "N/A"}
            </p>
          </div>
        </div>

        <div className={styles.listItem__divider} />

        {/* Order info */}
        <div className={styles.listItem__orderInfo}>
          <p className="small-text-size mb-0">Order Number:</p>
          <a
            className="purple--text small-text-size"
            style={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={() => setOrderId(sectionOrder?.order?.id)}
          >
            {sectionOrder?.order?.id}
          </a>
        </div>

        <div className={styles.listItem__divider} />

        {/* Orders & Notes */}
        <div className={styles.listItem__orders}>
          <p className="mb-0">
            <span>Orders: </span>
            <span className="dark--text" style={{ fontWeight: 600 }}>
              {sectionOrder?.docket_items?.map((item, index) => (
                <React.Fragment key={item?.id}>
                  {item?.quantity} x{" "}
                  {truncateString(item?.cart_item?.cart_item_name, 20)}
                  {index === sectionOrder?.docket_items?.length - 1 ? "" : ", "}
                </React.Fragment>
              ))}
            </span>
          </p>
          <p
            className={`small-text-size mb-0 ${styles.listItem__orders__notes}`}
          >
            Notes: {truncateString(sectionOrder?.order?.notes, 100) || "null"}
          </p>
          {sectionOrder?.started_at ? (
            <p className="small-text-size mb-0">
              Started: {defaultDateDisplay(sectionOrder.started_at)}
            </p>
          ) : null}
          {sectionOrder?.completed_at ? (
            <p className="small-text-size mb-0">
              Completed: {defaultDateDisplay(sectionOrder.completed_at)}
            </p>
          ) : null}
          {durMins > 0 ? (
            <div className="d-flex align-items-center" style={{ gap: "6px" }}>
              <p className="small-text-size mb-0">Est: {durMins} mins</p>
              <secondaryComponents.SectionDurationBadge
                started_at={sectionOrder?.started_at}
                completed_at={sectionOrder?.completed_at}
                expected_duration={sectionOrder?.expected_duration}
              />
            </div>
          ) : null}
        </div>

        {/* Actions */}
        <div className={styles.listItem__actions}>
          {sectionStatus === "RECEIVED" || sectionStatus === "IN_PROGRESS" ? (
            <>
              <primaryComponents.Button
                classNames="btn btn--outline"
                onClick={() => setIsPrintModal(true)}
                loadingText={"Processing..."}
              >
                {sectionOrder?.is_printed ? "Reprint" : "Print"}
              </primaryComponents.Button>
              <primaryComponents.Button
                onClick={() => setSectionDetails(sectionOrder)}
                classNames="btn btn--primary"
              >
                {sectionStatus === "RECEIVED" ? "Start" : "Mark as ready"}
              </primaryComponents.Button>
            </>
          ) : null}

          {sectionStatus === "COMPLETED" || sectionStatus === "CANCELLED" ? (
            <primaryComponents.Button
              classNames="btn btn--primary"
              style={{ width: "120px" }}
              onClick={() => setSectionDetails(sectionOrder)}
            >
              View Order
            </primaryComponents.Button>
          ) : null}
        </div>
      </div>

      {/* Section details side modal */}
      <secondaryComponents.SideModal
        isActive={sectionDetails !== null}
        width="500px"
        closeModal={() => setSectionDetails(null)}
      >
        <secondaryComponents.SectionDetailsWidget
          closeModal={() => setSectionDetails(null)}
          sectionDetails={sectionDetails}
        />
      </secondaryComponents.SideModal>

      {/* Order flow side modal */}
      <secondaryComponents.SideModal
        isActive={orderId !== null}
        width="1000px"
        closeModal={() => setOrderId(null)}
      >
        <TransactionDetailsV2
          orderId={orderId}
          showActionButtons={false}
          isOrderFlow={false}
          closeModal={() => setOrderId(null)}
        />
      </secondaryComponents.SideModal>

      {/* Print view */}
      {isEnabledPrint ? (
        <SectionView
          section_id={sectionOrder?.id}
          closePrint={() => setIsEnabledPrint(false)}
          isDocket={true}
          invalidateQueries={invalidateQueries}
        />
      ) : null}

      {/* Receipt terminal modal */}
      <secondaryComponents.SideModal
        width={500}
        isActive={isTerminal}
        closeModal={() => setIsTerminal(false)}
      >
        <ReceiptTerminalsV2
          isOrderFlow={false}
          sectionId={sectionOrder?.id}
          closeModal={() => setIsTerminal(false)}
        />
      </secondaryComponents.SideModal>

      {/* Print type modal */}
      <secondaryComponents.Modal
        width={400}
        isActive={isPrintModal}
        closeModal={() => setIsPrintModal(false)}
      >
        <PrintTypeOption
          printOnDeviceAction={() => setIsEnabledPrint(true)}
          printOnDevicePOS={() => setIsTerminal(true)}
          closeModal={() => setIsPrintModal(false)}
        />
      </secondaryComponents.Modal>
    </>
  );
};

export default SectionCardListItem;
